import { useEffect, useState } from "react";
import { ShieldAlert, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const TYPE_LABELS = { casse: "Casse / détérioration", manquant: "Matériel manquant", retour: "Incident de retour", autre: "Autre" };

export default function IncidentsPage() {
  const [incidents, setIncidents] = useState(null);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "casse", description: "", qty: 1, product_id: "" });

  const load = () => {
    api.get("/admin/logistics/incidents").then((r) => setIncidents(r.data)).catch(() => toast.error("Incidents indisponibles"));
    api.get("/catalog/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/admin/logistics/incidents", { ...form, qty: Number(form.qty), product_id: form.product_id || null, reservation_id: null });
      toast.success("Incident déclaré — quantité bloquée le cas échéant");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resolve = async (id) => {
    const decision = window.prompt("Décision Art Event (obligatoire — aucune retenue automatique) :");
    if (!decision || decision.length < 3) return;
    try {
      await api.post(`/admin/logistics/incidents/${id}/resolve`, { decision });
      toast.success("Incident résolu — décision historisée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!incidents) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="incidents-loading">Chargement…</div>;

  return (
    <div data-testid="admin-incidents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B10 — Cautions &amp; Incidents</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Cautions &amp; Incidents</h1>
          <p className="mt-1 text-sm text-muted-foreground">Aucune retenue sur caution ni remboursement automatique — chaque incident exige une décision humaine documentée.</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="incident-create">
          <Plus className="h-4 w-4" /> Déclarer un incident
        </button>
      </div>

      <div className="mt-6 card-ae" data-testid="incidents-list">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg text-deepgreen">Incidents</h2>
        </div>
        {incidents.length === 0 ? (
          <div className="p-5"><EmptyState icon={ShieldAlert} title="Aucun incident">Les incidents déclarés (retours, casse, manquant) apparaîtront ici avec leur décision.</EmptyState></div>
        ) : (
          <ul className="divide-y divide-border">
            {incidents.map((inc) => (
              <li key={inc.id} className="px-5 py-4" data-testid={`incident-${inc.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <StatusBadge status={inc.status === "en_cours" ? "En attente" : "Répondu"} />
                  <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">{TYPE_LABELS[inc.type] || inc.type}</span>
                  {inc.ref && <span className="text-xs font-semibold text-deepgreen">{inc.ref}</span>}
                  <span className="text-xs text-muted-foreground">{(inc.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {inc.status === "en_cours" && (
                    <button type="button" onClick={() => resolve(inc.id)} className="ml-auto btn-primary-ae !px-4 !py-2 !text-[11px]" data-testid={`incident-resolve-${inc.id}`}>
                      <CheckCircle2 className="h-4 w-4" /> Décision Art Event
                    </button>
                  )}
                </div>
                <ul className="mt-2 space-y-1 text-sm text-foreground/80">
                  {(inc.lines || [inc.description]).map((l, i) => <li key={i}>• {l}</li>)}
                </ul>
                {inc.decision && <p className="mt-2 rounded-lg bg-sage-light px-3 py-2 text-xs text-deepgreen"><span className="font-bold">Décision :</span> {inc.decision}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 rounded-lg border-2 border-dashed border-sage bg-cream p-5 text-sm" data-testid="cautions-note">
        <p className="font-bold text-deepgreen">Cautions — séparées du chiffre d'affaires</p>
        <p className="mt-1 text-muted-foreground">
          Les montants de caution sont calculés par article et visibles sur chaque réservation (jamais confondus avec le total commercial).
          Le mécanisme de dépôt (empreinte, chèque, virement) reste À DÉFINIR TECHNIQUEMENT — aucune retenue automatique ne sera jamais appliquée.
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="incident-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">Déclarer un incident</DialogTitle>
            <DialogDescription>Casse, manquant ou autre — la quantité concernée devient indisponible jusqu'à décision.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="inc-type">Type</label>
                <select id="inc-type" className="input-ae" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="incident-type">
                  {Object.entries(TYPE_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </div>
              <div><label className="label-ae" htmlFor="inc-qty">Quantité</label><input id="inc-qty" type="number" min="1" className="input-ae" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="incident-qty" /></div>
            </div>
            <div><label className="label-ae" htmlFor="inc-product">Article concerné (facultatif)</label>
              <select id="inc-product" className="input-ae" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="incident-product">
                <option value="">Aucun</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">Si un article est lié, la quantité devient immédiatement indisponible jusqu'à résolution.</p>
            </div>
            <div><label className="label-ae" htmlFor="inc-desc">Description</label><textarea id="inc-desc" className="input-ae min-h-24" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="incident-description" /></div>
            <button type="button" onClick={submit} className="btn-primary-ae modal-sticky-btn w-full" data-testid="incident-submit">Déclarer</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
